import { useState, useCallback, useMemo } from 'react';
import { collection, query, getDocs, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Pizza, Pizzeria } from '../types';
import { SearchFilters } from '../types/searchTypes';

export function useFirestore() {
  const [isOffline, setIsOffline] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number; zoom: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFirestoreError = useCallback((err: any) => {
    if (err.code === 'unavailable') {
      setIsOffline(true);
      return 'Operando in modalità offline. I dati potrebbero non essere aggiornati.';
    }
    return err.message;
  }, []);

  const hasPizzeriaVisiblePizzas = useCallback((pizzeria: Pizzeria): boolean => {
    return pizzeria.pizzas.some(pizzaId => !pizzeria.hiddenPizzas?.includes(pizzaId));
  }, []);

  const searchPizzerias = useCallback(async (filters: SearchFilters) => {
    try {
      let q;
      
      if (filters.type === 'owner') {
        q = query(
          collection(db, 'pizzerie'),
          where('ownerId', '==', filters.query)
        );
      } else {
        q = collection(db, 'pizzerie');
      }

      const snapshot = await getDocs(q);
      let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Pizzeria[];

      if (filters.type === 'pizzeria') {
        const searchTerm = filters.query.toLowerCase();
        results = results.filter(pizzeria => 
          pizzeria.name.toLowerCase().includes(searchTerm) ||
          pizzeria.city.toLowerCase().includes(searchTerm) ||
          pizzeria.address.toLowerCase().includes(searchTerm)
        );
      }

      return results.filter(pizzeria => hasPizzeriaVisiblePizzas(pizzeria));
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      throw new Error(errorMessage);
    }
  }, [hasPizzeriaVisiblePizzas, handleFirestoreError]);

  const searchPizzas = useCallback(async (filters: SearchFilters) => {
    try {
      const pizzeriasSnapshot = await getDocs(collection(db, 'pizzerie'));
      const pizzerias = pizzeriasSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      })) as Pizzeria[];

      const q = collection(db, 'pizze');
      const snapshot = await getDocs(q);
      let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Pizza[];

      if (filters.query) {
        const searchTerm = filters.query.toLowerCase();
        results = results.filter(pizza => 
          pizza.name.toLowerCase().includes(searchTerm) ||
          pizza.ingredients.some(i => i.toLowerCase().includes(searchTerm))
        );
      }

      results = results.filter(pizza => 
        pizzerias.some(pizzeria => 
          pizzeria.pizzas.includes(pizza.id) && !pizzeria.hiddenPizzas?.includes(pizza.id)
        )
      );

      return results;
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      throw new Error(errorMessage);
    }
  }, [handleFirestoreError]);

  const getPizzeria = useCallback(async (id: string) => {
    try {
      const docRef = doc(db, 'pizzerie', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const pizzeria = { id: docSnap.id, ...docSnap.data() } as Pizzeria;
        if (hasPizzeriaVisiblePizzas(pizzeria)) {
          return pizzeria;
        }
      }
      return null;
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      throw new Error(errorMessage);
    }
  }, [hasPizzeriaVisiblePizzas, handleFirestoreError]);

  const getPizza = useCallback(async (id: string) => {
    try {
      const docRef = doc(db, 'pizze', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const pizza = { id: docSnap.id, ...docSnap.data() } as Pizza;
        
        const pizzeriasSnapshot = await getDocs(collection(db, 'pizzerie'));
        const pizzerias = pizzeriasSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        })) as Pizzeria[];
        
        const isVisibleInAnyPizzeria = pizzerias.some(pizzeria => 
          pizzeria.pizzas.includes(pizza.id) && !pizzeria.hiddenPizzas?.includes(pizza.id)
        );

        if (isVisibleInAnyPizzeria) {
          // Aggiorna l'array pizzerias con solo le pizzerie dove la pizza è effettivamente visibile
          pizza.pizzerias = pizzerias
            .filter(pizzeria => 
              pizzeria.pizzas.includes(pizza.id) && !pizzeria.hiddenPizzas?.includes(pizza.id)
            )
            .map(pizzeria => pizzeria.id);
          return pizza;
        }
      }
      return null;
    } catch (err) {
      const errorMessage = handleFirestoreError(err);
      throw new Error(errorMessage);
    }
  }, [handleFirestoreError]);

  return {
    isOffline,
    mapCenter,
    searchPizzerias,
    searchPizzas,
    getPizzeria,
    getPizza,
    loading,
    error
  };
}